import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Html5Qrcode } from 'html5-qrcode';
import { analyzeFoodImage, lookupBarcode } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import {
    Camera, ScanBarcode, X, Check, Loader2, Upload,
    Utensils, Flame, Beef, Wheat, Droplet, AlertCircle, ChevronDown, ChevronUp, Info
} from 'lucide-react';

export function FoodScanner({ open, onOpenChange, onFoodScanned }) {
    const { language } = useLanguage();
    const [mode, setMode] = useState(null); // 'photo' | 'barcode' | null
    const [scanning, setScanning] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [manualEan, setManualEan] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [selectedMealType, setSelectedMealType] = useState(null);
    const [showMealSelector, setShowMealSelector] = useState(false);
    
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const scannerRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        return () => {
            stopCamera();
            stopBarcodeScanner();
        };
    }, []);

    useEffect(() => {
        if (!open) {
            stopCamera();
            stopBarcodeScanner();
            setMode(null);
            setResult(null);
            setCapturedImage(null);
            setManualEan('');
        }
    }, [open]);

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    };

    const stopBarcodeScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
            } catch {
                // Scanner already stopped — non-fatal
            }
            scannerRef.current = null;
        }
    };

    const startPhotoMode = async () => {
        setMode('photo');
        setResult(null);
        setCapturedImage(null);
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Camera error:', error);
            toast.error(language === 'de' ? 'Kamera konnte nicht gestartet werden' : 'Could not start camera');
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;
        
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        const base64 = imageData.split(',')[1];
        
        setCapturedImage(imageData);
        stopCamera();
        analyzePhoto(base64);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (event) => {
            const imageData = event.target.result;
            const base64 = imageData.split(',')[1];
            setCapturedImage(imageData);
            setMode('photo');
            analyzePhoto(base64);
        };
        reader.readAsDataURL(file);
    };

    const analyzePhoto = async (base64) => {
        setAnalyzing(true);
        try {
            const response = await analyzeFoodImage(base64);
            setResult({
                type: 'photo',
                ...response.data
            });
        } catch (error) {
            console.error('Analysis error:', error);
            toast.error(language === 'de' ? 'Analyse fehlgeschlagen' : 'Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    };

    const startBarcodeMode = async () => {
        setMode('barcode');
        setResult(null);
        setScanning(true);
        
        try {
            await stopBarcodeScanner();
            
            const html5QrCode = new Html5Qrcode("barcode-reader");
            scannerRef.current = html5QrCode;
            
            await html5QrCode.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 150 },
                    aspectRatio: 1.5
                },
                async (decodedText) => {
                    await stopBarcodeScanner();
                    setScanning(false);
                    lookupProduct(decodedText);
                },
                (error) => {
                    // Ignore scan errors
                }
            );
        } catch (error) {
            console.error('Scanner error:', error);
            setScanning(false);
            toast.error(language === 'de' ? 'Scanner konnte nicht gestartet werden' : 'Could not start scanner');
        }
    };

    const lookupProduct = async (ean) => {
        setAnalyzing(true);
        setManualEan('');
        try {
            const response = await lookupBarcode(ean);
            setResult({
                type: 'barcode',
                ...response.data
            });
        } catch (error) {
            console.error('Lookup error:', error);
            if (error.response?.status === 404) {
                toast.error(language === 'de' ? 'Produkt nicht gefunden' : 'Product not found');
            } else {
                toast.error(language === 'de' ? 'Fehler bei Produktsuche' : 'Product lookup failed');
            }
        } finally {
            setAnalyzing(false);
        }
    };

    const handleManualEanSubmit = (e) => {
        e.preventDefault();
        if (manualEan.length >= 8) {
            lookupProduct(manualEan);
        }
    };

    const addToLog = () => {
        if (!result) return;
        
        // Show meal type selector first
        if (!selectedMealType) {
            setShowMealSelector(true);
            return;
        }
        
        let foodData;
        if (result.type === 'photo') {
            foodData = {
                name: result.name,
                calories: result.calories,
                protein: result.protein,
                carbs: result.carbs,
                fat: result.fat,
                fiber: result.fiber || 0,
                meal_type: selectedMealType,
                source: 'photo_analysis'
            };
        } else {
            // Barcode result - use per 100g values
            const nutrients = result.nutrients_per_100g;
            foodData = {
                name: `${result.brand ? result.brand + ' ' : ''}${result.name}`,
                calories: nutrients.calories,
                protein: nutrients.protein,
                carbs: nutrients.carbs,
                fat: nutrients.fat,
                meal_type: selectedMealType,
                source: 'barcode',
                ean: result.ean
            };
        }
        
        onFoodScanned(foodData);
        setSelectedMealType(null);
        setShowMealSelector(false);
        onOpenChange(false);
    };
    
    const selectMealType = (type) => {
        setSelectedMealType(type);
        setShowMealSelector(false);
        // Trigger addToLog again with selected meal type
        setTimeout(() => {
            // Call the actual save
            let foodData;
            if (result.type === 'photo') {
                foodData = {
                    name: result.name,
                    calories: result.calories,
                    protein: result.protein,
                    carbs: result.carbs,
                    fat: result.fat,
                    fiber: result.fiber || 0,
                    meal_type: type,
                    source: 'photo_analysis'
                };
            } else {
                const nutrients = result.nutrients_per_100g;
                foodData = {
                    name: `${result.brand ? result.brand + ' ' : ''}${result.name}`,
                    calories: nutrients.calories,
                    protein: nutrients.protein,
                    carbs: nutrients.carbs,
                    fat: nutrients.fat,
                    meal_type: type,
                    source: 'barcode',
                    ean: result.ean
                };
            }
            onFoodScanned(foodData);
            setSelectedMealType(null);
            onOpenChange(false);
        }, 100);
    };

    const retake = () => {
        setResult(null);
        setCapturedImage(null);
        if (mode === 'photo') {
            startPhotoMode();
        } else {
            startBarcodeMode();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {mode === 'photo' && <Camera className="w-5 h-5 text-violet-500" />}
                        {mode === 'barcode' && <ScanBarcode className="w-5 h-5 text-emerald-500" />}
                        {!mode && <Utensils className="w-5 h-5 text-orange-500" />}
                        {mode === 'photo' 
                            ? (language === 'de' ? 'Foto-Analyse' : 'Photo Analysis')
                            : mode === 'barcode'
                            ? (language === 'de' ? 'Barcode Scanner' : 'Barcode Scanner')
                            : (language === 'de' ? 'Essen erfassen' : 'Log Food')}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'photo' 
                            ? (language === 'de' ? 'Fotografiere dein Essen für eine AI-Analyse' : 'Take a photo of your food for AI analysis')
                            : mode === 'barcode'
                            ? (language === 'de' ? 'Scanne den Barcode auf der Verpackung' : 'Scan the barcode on the packaging')
                            : (language === 'de' ? 'Wähle eine Methode' : 'Choose a method')}
                    </DialogDescription>
                </DialogHeader>

                {/* Mode Selection */}
                {!mode && (
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <Button
                            variant="outline"
                            className="h-32 flex-col gap-3 hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                            onClick={startPhotoMode}
                        >
                            <Camera className="w-10 h-10 text-violet-500" />
                            <span className="font-medium">
                                {language === 'de' ? 'Foto aufnehmen' : 'Take Photo'}
                            </span>
                        </Button>
                        <Button
                            variant="outline"
                            className="h-32 flex-col gap-3 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                            onClick={startBarcodeMode}
                        >
                            <ScanBarcode className="w-10 h-10 text-emerald-500" />
                            <span className="font-medium">
                                {language === 'de' ? 'Barcode scannen' : 'Scan Barcode'}
                            </span>
                        </Button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*"
                            className="hidden"
                        />
                        <Button
                            variant="ghost"
                            className="col-span-2 gap-2"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload className="w-4 h-4" />
                            {language === 'de' ? 'Bild aus Galerie wählen' : 'Choose from gallery'}
                        </Button>
                    </div>
                )}

                {/* Photo Mode */}
                {mode === 'photo' && !result && (
                    <div className="space-y-4">
                        {!capturedImage ? (
                            <>
                                <div className="relative aspect-[4/3] bg-black rounded-xl overflow-hidden">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 pointer-events-none">
                                        <div className="absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-white/50 rounded-tl-lg" />
                                        <div className="absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-white/50 rounded-tr-lg" />
                                        <div className="absolute bottom-4 left-4 w-12 h-12 border-l-2 border-b-2 border-white/50 rounded-bl-lg" />
                                        <div className="absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-white/50 rounded-br-lg" />
                                    </div>
                                </div>
                                <canvas ref={canvasRef} className="hidden" />
                                <div className="flex gap-2">
                                    <Button variant="outline" className="flex-1" onClick={() => setMode(null)}>
                                        <X className="w-4 h-4 mr-2" />
                                        {language === 'de' ? 'Abbrechen' : 'Cancel'}
                                    </Button>
                                    <Button className="flex-1" onClick={capturePhoto}>
                                        <Camera className="w-4 h-4 mr-2" />
                                        {language === 'de' ? 'Aufnehmen' : 'Capture'}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="relative aspect-[4/3] bg-black rounded-xl overflow-hidden">
                                <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
                                {analyzing && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                        <div className="text-center text-foreground">
                                            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                                            <p>{language === 'de' ? 'Analysiere...' : 'Analyzing...'}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Barcode Mode */}
                {mode === 'barcode' && !result && (
                    <div className="space-y-4">
                        <div id="barcode-reader" className="rounded-xl overflow-hidden" style={{ minHeight: '250px' }} />
                        
                        {scanning && (
                            <p className="text-center text-sm text-muted-foreground animate-pulse">
                                {language === 'de' ? 'Halte den Barcode vor die Kamera...' : 'Hold barcode in front of camera...'}
                            </p>
                        )}

                        {/* Manual EAN input */}
                        <form onSubmit={handleManualEanSubmit} className="flex gap-2">
                            <Input
                                placeholder={language === 'de' ? 'EAN manuell eingeben' : 'Enter EAN manually'}
                                value={manualEan}
                                onChange={(e) => setManualEan(e.target.value)}
                                className="flex-1"
                            />
                            <Button type="submit" disabled={manualEan.length < 8 || analyzing}>
                                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                        </form>

                        <Button variant="outline" className="w-full" onClick={() => { stopBarcodeScanner(); setMode(null); }}>
                            <X className="w-4 h-4 mr-2" />
                            {language === 'de' ? 'Abbrechen' : 'Cancel'}
                        </Button>
                    </div>
                )}

                {/* Result Display */}
                {result && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        {/* Product/Food Info */}
                        <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                            {result.type === 'barcode' && result.image_url && (
                                <img 
                                    src={result.image_url} 
                                    alt={result.name}
                                    className="w-20 h-20 object-contain mx-auto rounded-lg bg-white"
                                />
                            )}
                            {result.type === 'photo' && capturedImage && (
                                <img 
                                    src={capturedImage} 
                                    alt="Food"
                                    className="w-full h-32 object-cover rounded-lg"
                                />
                            )}
                            
                            <div className="text-center">
                                {result.brand && (
                                    <p className="text-sm text-muted-foreground">{result.brand}</p>
                                )}
                                <h3 className="font-semibold text-lg">{result.name}</h3>
                                {result.description && (
                                    <p className="text-sm text-muted-foreground mt-1">{result.description}</p>
                                )}
                                
                                {/* Badges Row */}
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {result.comparison?.description && (
                                        <Badge variant="outline" className="bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300">
                                            {result.comparison.description}
                                        </Badge>
                                    )}
                                    {result.confidence && (
                                        <Badge variant="outline" className={
                                            result.confidence === 'high' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300' :
                                            result.confidence === 'low' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300' :
                                            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300'
                                        }>
                                            {language === 'de' ? 'Konfidenz' : 'Confidence'}: {result.confidence}
                                        </Badge>
                                    )}
                                    {result.nutriscore && (
                                        <Badge 
                                            className={
                                                result.nutriscore === 'a' ? 'bg-green-500' :
                                                result.nutriscore === 'b' ? 'bg-lime-500' :
                                                result.nutriscore === 'c' ? 'bg-yellow-500' :
                                                result.nutriscore === 'd' ? 'bg-orange-500' : 'bg-red-500'
                                            }
                                        >
                                            Nutri-Score {result.nutriscore.toUpperCase()}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Breakdown for photo analysis - with detailed explanations */}
                        {result.type === 'photo' && result.breakdown?.length > 0 && (
                            <div className="space-y-2">
                                {/* Collapsible header */}
                                <button 
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="w-full p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                >
                                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200 flex items-center gap-2">
                                        <Info className="w-4 h-4" />
                                        🍽️ {language === 'de' ? 'Makronährstoffe pro Gang (Details)' : 'Macros per Course (Details)'}
                                    </span>
                                    {showDetails ? (
                                        <ChevronUp className="w-4 h-4 text-blue-600" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4 text-blue-600" />
                                    )}
                                </button>
                                
                                {/* Expandable content */}
                                <AnimatePresence>
                                    {showDetails && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="space-y-3 pt-1">
                                                {result.breakdown.map((item, idx) => (
                                                    <div key={idx} className="p-3 rounded-lg bg-slate-50 dark:bg-card border border-slate-200 dark:border-border">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className="font-medium text-slate-800 dark:text-slate-200">
                                                                {item.name}
                                                            </span>
                                                            <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                                                                {item.calories_min && item.calories_max && item.calories_min !== item.calories_max
                                                                    ? `≈ ${item.calories_min}–${item.calories_max} kcal`
                                                                    : `≈ ${item.calories} kcal`
                                                                }
                                                            </span>
                                                        </div>
                                                        
                                                        {/* Macro details with ranges */}
                                                        <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <Beef className="w-3 h-3 text-red-500" />
                                                                <span className="text-muted-foreground">
                                                                    {item.protein_min && item.protein_max && item.protein_min !== item.protein_max
                                                                        ? `${item.protein_min}–${item.protein_max}g`
                                                                        : `${Math.round(item.protein || 0)}g`
                                                                    }
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Droplet className="w-3 h-3 text-yellow-500" />
                                                                <span className="text-muted-foreground">
                                                                    {item.fat_min && item.fat_max && item.fat_min !== item.fat_max
                                                                        ? `${item.fat_min}–${item.fat_max}g`
                                                                        : `${Math.round(item.fat || 0)}g`
                                                                    }
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Wheat className="w-3 h-3 text-amber-500" />
                                                                <span className="text-muted-foreground">
                                                                    {item.carbs_min && item.carbs_max && item.carbs_min !== item.carbs_max
                                                                        ? `${item.carbs_min}–${item.carbs_max}g`
                                                                        : `${Math.round(item.carbs || 0)}g`
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Explanation */}
                                                        {item.explanation && (
                                                            <p className="text-xs text-muted-foreground italic border-t border-slate-200 dark:border-border pt-2 mt-2">
                                                                💡 {item.explanation}
                                                            </p>
                                                        )}
                                                        
                                                        {/* Portion info */}
                                                        {item.portion && (
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                📏 {language === 'de' ? 'Portion' : 'Portion'}: {item.portion}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                                
                                                {/* Summary / Kurzfazit */}
                                                {result.summary && Object.keys(result.summary).length > 0 && (
                                                    <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800">
                                                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                                                            🧠 {language === 'de' ? 'Kurzfazit' : 'Quick Summary'}
                                                        </p>
                                                        <ul className="space-y-1 text-xs text-emerald-700 dark:text-emerald-300">
                                                            {result.summary.protein_note && (
                                                                <li className="flex items-start gap-2">
                                                                    <Beef className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                    <span>{result.summary.protein_note}</span>
                                                                </li>
                                                            )}
                                                            {result.summary.fat_note && (
                                                                <li className="flex items-start gap-2">
                                                                    <Droplet className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                    <span>{result.summary.fat_note}</span>
                                                                </li>
                                                            )}
                                                            {result.summary.carbs_note && (
                                                                <li className="flex items-start gap-2">
                                                                    <Wheat className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                    <span>{result.summary.carbs_note}</span>
                                                                </li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {/* Total ranges */}
                                                {result.ranges && (
                                                    <div className="p-3 rounded-lg bg-slate-100 dark:bg-secondary border border-slate-200 dark:border-border">
                                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-2">
                                                            🔢 {language === 'de' ? 'Gesamte Makronährstoffe' : 'Total Macros'}
                                                        </p>
                                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-muted-foreground">Kalorien</span>
                                                                <span className="font-medium">{result.ranges.calories}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-muted-foreground">Protein</span>
                                                                <span className="font-medium">{result.ranges.protein}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-muted-foreground">Fett</span>
                                                                <span className="font-medium">{result.ranges.fat}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-muted-foreground">Kohlenhydrate</span>
                                                                <span className="font-medium">{result.ranges.carbs}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                        
                        {/* Assumptions */}
                        {result.type === 'photo' && result.assumptions && (
                            <div className="p-2 rounded-lg bg-slate-100 dark:bg-card text-xs text-muted-foreground">
                                <p>📋 {result.assumptions}</p>
                            </div>
                        )}

                        {/* Nutrients */}
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                { 
                                    icon: Flame, 
                                    label: 'kcal', 
                                    value: result.type === 'photo' ? result.calories : result.nutrients_per_100g.calories,
                                    color: 'text-orange-500'
                                },
                                { 
                                    icon: Beef, 
                                    label: language === 'de' ? 'Protein' : 'Protein', 
                                    value: result.type === 'photo' ? result.protein : result.nutrients_per_100g.protein,
                                    unit: 'g',
                                    color: 'text-red-500'
                                },
                                { 
                                    icon: Wheat, 
                                    label: language === 'de' ? 'Kohlenh.' : 'Carbs', 
                                    value: result.type === 'photo' ? result.carbs : result.nutrients_per_100g.carbs,
                                    unit: 'g',
                                    color: 'text-amber-500'
                                },
                                { 
                                    icon: Droplet, 
                                    label: language === 'de' ? 'Fett' : 'Fat', 
                                    value: result.type === 'photo' ? result.fat : result.nutrients_per_100g.fat,
                                    unit: 'g',
                                    color: 'text-blue-500'
                                },
                            ].map((item) => (
                                <div key={item.label} className="text-center p-3 rounded-xl bg-muted/30">
                                    <item.icon className={`w-5 h-5 mx-auto mb-1 ${item.color}`} />
                                    <p className="font-bold">{item.value}{item.unit || ''}</p>
                                    <p className="text-xs text-muted-foreground">{item.label}</p>
                                </div>
                            ))}
                        </div>

                        {result.type === 'barcode' && (
                            <p className="text-xs text-center text-muted-foreground">
                                {language === 'de' ? 'Werte pro 100g' : 'Values per 100g'}
                            </p>
                        )}
                        
                        {result.type === 'photo' && result.disclaimer && (
                            <p className="text-xs text-center text-muted-foreground">
                                ⚠️ {result.disclaimer}
                            </p>
                        )}

                        {/* Meal Type Selector */}
                        {showMealSelector && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 rounded-xl bg-violet-50 dark:bg-violet-950/30 border-2 border-violet-300 dark:border-violet-700"
                            >
                                <p className="text-sm font-medium text-center mb-3 text-violet-800 dark:text-violet-200">
                                    🍽️ {language === 'de' ? 'Zu welcher Mahlzeit gehört das?' : 'Which meal is this?'}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { type: 'breakfast', label: language === 'de' ? '🌅 Frühstück' : '🌅 Breakfast', color: 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/50 dark:hover:bg-amber-800/50 text-amber-800 dark:text-amber-200' },
                                        { type: 'lunch', label: language === 'de' ? '☀️ Mittagessen' : '☀️ Lunch', color: 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/50 dark:hover:bg-orange-800/50 text-orange-800 dark:text-orange-200' },
                                        { type: 'dinner', label: language === 'de' ? '🌙 Abendessen' : '🌙 Dinner', color: 'bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:hover:bg-indigo-800/50 text-indigo-800 dark:text-indigo-200' },
                                        { type: 'snack', label: language === 'de' ? '🍎 Snack' : '🍎 Snack', color: 'bg-green-100 hover:bg-green-200 dark:bg-green-900/50 dark:hover:bg-green-800/50 text-green-800 dark:text-green-200' }
                                    ].map(meal => (
                                        <button
                                            key={meal.type}
                                            onClick={() => selectMealType(meal.type)}
                                            className={`p-3 rounded-lg font-medium transition-all ${meal.color} border border-transparent hover:border-current`}
                                        >
                                            {meal.label}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={retake}>
                                {language === 'de' ? 'Neu erfassen' : 'Retake'}
                            </Button>
                            <Button className="flex-1" onClick={addToLog} disabled={showMealSelector}>
                                <Check className="w-4 h-4 mr-2" />
                                {language === 'de' ? 'Hinzufügen' : 'Add to log'}
                            </Button>
                        </div>
                    </motion.div>
                )}
            </DialogContent>
        </Dialog>
    );
}
